import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DocumentsCollection,
  LoadingCard,
} from '@folio/components';
import {
  DocumentViewer,
  type DocumentViewerDocument,
  FraudStatusBadge,
  useDocumentViewer,
} from '@folio/components/features/document-viewer';
import { ErrorMessage } from '@folio/components/module-elements/ErrorMessage';
import { Stack } from '@folio/components/ui/flex';
import { Text } from '@folio/components/ui/text';
import { useUserPermissions } from '@folio/hooks/useUserPermissions';
import { useAssertedParams } from '@folio/lib';
import { useQuery } from '@tanstack/react-query';
import {
  type ClaimManagementStateDto,
  type CorrectPolicyDocumentFraudVerdictInputBody,
  type DocumentFraudScoreDto,
  claimManagementControllerDocumentFraudScoresQueryOptions,
  claimManagementControllerGetQueryOptions,
  documentsControllerGetDocumentsQueryOptions,
  keyValueFieldsToObject,
  useClaimManagementControllerCorrectDocumentFraudVerdict,
} from 'korint-sdk';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentReviewFilters } from '../DocumentsUploadPolicyModule/DocumentReviewFilters';
import type { DocumentReviewFilter } from '../DocumentsUploadPolicyModule/document-review';
import { claimDocumentGroups, claimDocumentReview } from './claimDocumentGroups';

function ClaimDocuments({ claim }: { claim: ClaimManagementStateDto }) {
  const { t } = useTranslation();
  const [reviewFilter, setReviewFilter] = useState<DocumentReviewFilter>('all');
  const documentIds = claim.documents.flatMap(({ documentId }) => documentId ?? []);

  const {
    data: storedDocuments,
    isLoading: areDocumentsLoading,
    isError: hasDocumentsError,
  } = useQuery({
    ...documentsControllerGetDocumentsQueryOptions({ ids: documentIds }),
    enabled: documentIds.length > 0,
  });
  const {
    data: fraud,
    isLoading: isFraudLoading,
    isError: hasFraudError,
    refetch: refetchFraud,
  } = useQuery(claimManagementControllerDocumentFraudScoresQueryOptions(claim.id));

  const { can } = useUserPermissions({
    productId: claim.productId,
    actions: ['CORRECT_DOCUMENT_FRAUD_VERDICT'],
  });
  const { mutate: correctFraudVerdict, isPending: isCorrectingFraudVerdict } =
    useClaimManagementControllerCorrectDocumentFraudVerdict({
      mutation: { onSuccess: () => refetchFraud() },
    });

  const groups = useMemo(
    () =>
      claimDocumentGroups({
        slots: claim.documents,
        documents: (storedDocuments?.documents ?? []).map(
          ({ metadata, ...document }): DocumentViewerDocument => ({
            ...document,
            metadata: keyValueFieldsToObject(metadata ?? []),
          }),
        ),
      }),
    [claim.documents, storedDocuments],
  );
  const scoresByDocumentId = useMemo(
    () =>
      new Map(
        (fraud?.scores ?? []).map((score): [string, DocumentFraudScoreDto] => [
          score.documentId,
          score,
        ]),
      ),
    [fraud],
  );
  const review = claimDocumentReview({ groups, scoresByDocumentId, filter: reviewFilter });
  const viewerDocuments = useMemo(
    () =>
      review.groups
        .flatMap(({ documents }) => documents)
        .filter(({ signedUrl }) => Boolean(signedUrl)),
    [review.groups],
  );
  const { selectedDocument, selectDocument, closeViewer } = useDocumentViewer(viewerDocuments);

  const renderFraudBadge = ({ id }: DocumentViewerDocument) => {
    const score = scoresByDocumentId.get(id);
    return score ? <FraudStatusBadge fraud={score} /> : null;
  };

  // The claim endpoint takes no `expectedUpdatedAt`, and the gateway rejects unknown fields.
  const handleCorrectFraudVerdict = (
    documentId: string,
    { verdict, notes }: CorrectPolicyDocumentFraudVerdictInputBody,
  ) => correctFraudVerdict({ id: claim.id, documentId, data: { verdict, notes } });

  return (
    <>
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="text-lg md:text-xl">{t('documentReview.title')}</CardTitle>
        </CardHeader>
        <Stack asChild>
          <CardContent className="p-4 md:p-6 md:pt-0">
            <DocumentReviewFilters
              summary={{
                ...review.counts,
                isLoading: areDocumentsLoading || isFraudLoading,
                hasError: hasDocumentsError || hasFraudError,
              }}
              filter={reviewFilter}
              onFilterChange={setReviewFilter}
            />
            {areDocumentsLoading ? (
              <LoadingCard />
            ) : groups.length === 0 ? (
              <Text size="sm" tone="muted">
                {t('claimDocuments.empty')}
              </Text>
            ) : (
              <DocumentsCollection
                groups={review.groups}
                emptyCategories={reviewFilter === 'attention' ? 'hidden' : 'required'}
                onView={selectDocument}
                renderBadges={renderFraudBadge}
                afterUpload={
                  reviewFilter === 'attention' && review.counts.attentionCount === 0 ? (
                    <Text size="sm" tone="muted">
                      {t('documentReview.noAttention')}
                    </Text>
                  ) : reviewFilter === 'missing' && review.counts.missingRequiredCount === 0 ? (
                    <Text size="sm" tone="muted">
                      {t('documentReview.noMissing')}
                    </Text>
                  ) : null
                }
              />
            )}
          </CardContent>
        </Stack>
      </Card>
      <DocumentViewer
        productId={claim.productId}
        displayContext="broker"
        document={selectedDocument}
        documents={viewerDocuments}
        onClose={closeViewer}
        onDocumentChange={selectDocument}
        fraud={selectedDocument ? scoresByDocumentId.get(selectedDocument.id) : undefined}
        fraudPending={isFraudLoading}
        onCorrectFraudVerdict={
          can.CORRECT_DOCUMENT_FRAUD_VERDICT ? handleCorrectFraudVerdict : undefined
        }
        isCorrectingFraudVerdict={isCorrectingFraudVerdict}
      />
    </>
  );
}

export function ClaimDocumentsModule() {
  const { t } = useTranslation();
  const { claimId } = useAssertedParams<{ claimId: string }>();

  const {
    data: claim,
    isLoading,
    isError,
  } = useQuery(claimManagementControllerGetQueryOptions(claimId));

  if (isLoading) {
    return <LoadingCard />;
  }

  if (isError || !claim) {
    return <ErrorMessage>{t('claimInformation.error')}</ErrorMessage>;
  }

  return <ClaimDocuments claim={claim} />;
}
